import { Product } from '../types/product';
import { ProductMasterData } from '../types/product_master_data/productMasterData';

export interface ProductQuantity {
  unitQuantity: bigint;
}

export namespace ProductUtil {
  export function getGroupedQuantity(
    productMasterData: Map<string, ProductMasterData>,
    products: Map<string, Map<string, Product>>,
    handlingUnitConnections: Map<string, Set<string>>,
  ) {
    const items: Map<string, ProductQuantity> = new Map();
    for (const key of products.keys()) {
      const connections = handlingUnitConnections.get(key);
      const values = [...(products.get(key)?.values() || [])];
      const pmd = productMasterData.get(key);
      const unitQuantity = values
        .filter(i => !i.kitId || pmd?.kitManaged)
        .reduce((sum, item) => sum + BigInt(item.unitQuantity), BigInt(0));
      let unitQuantityByHandlingUnit = BigInt(0);

      if (connections) {
        for (const connection of connections) {
          if (!connection.includes('::')) continue;
          const [parentId, multiplier] = connection.split('::');
          const parentQty = [...(products.get(parentId)?.values() || [])].reduce(
            (sum, item) => sum + BigInt(item.unitQuantity),
            BigInt(0),
          );
          unitQuantityByHandlingUnit += parentQty * BigInt(multiplier);
        }
      } else if (!pmd?.kitManaged) {
        const quantity = values.filter(i => i.kitId).reduce((sum, item) => sum + BigInt(item.unitQuantity), BigInt(0));
        unitQuantityByHandlingUnit += quantity;
      }

      items.set(key, { unitQuantity });
    }

    return items;
  }

  export function getGroupedProducts(
    products: Map<string, Map<string, Product>>,
    handlingUnitConnections: Map<string, Set<string>>,
    pmdId: string,
  ) {
    const items = [...(products.get(pmdId)?.values() || [])];
    const connections = handlingUnitConnections.get(pmdId);
    if (!connections) return items;

    for (const connection of connections) {
      if (!connection.includes('::')) continue;
      const [parentId, multiplier] = connection.split('::');
      items.push(...[...(products.get(parentId)?.values() || [])]);
    }
    return items;
  }
}

function getIdentifierValueById(input: string | undefined, id: string) {
  if (!input) return undefined;
  const regexStr = `${id}::(.*?)(?=::|$)`;
  const found = [...input.matchAll(new RegExp(regexStr, 'g'))];
  if (!found.length) return undefined;
  return found[0][1];
}

function getAllIdentifierValues(input: string | undefined) {
  const identifiers = new Map<string, string>();
  if (!input) return identifiers;
  const all = input.split('::');
  if (all.length === 1) return identifiers;
  for (let i = 0; i < all.length; i += 2) {
    const id = all[i];
    const value = all[i + 1];
    identifiers.set(id, value);
  }
  return identifiers;
}

function createIdentifierString(values: Map<string, string>) {
  if (!values.size) return undefined;
  return [...values.entries()]
    .sort((a, b) => (a[0] > b[0] ? 1 : -1))
    .map(v => `${v[0]}::${v[1]}`)
    .join('::');
}

export const ProductIdentifierUtil = {
  getIdentifierValueById,
  getAllIdentifierValues,
  createIdentifierString,
};

export const getQuantityByBinForProduct = (products: Product[], lotId?: string) => {
  const map = new Map<string, number>();
  products
    .filter(item => !lotId || item.lotId === lotId)
    .forEach(p => {
      map.set(p.binId, (map.get(p.binId) || 0) + (parseFloat(p.unitQuantity || '0') || 0));
    });
  return map;
};
